/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'

// Project Components
import {ListMenuContent} from './list-menu-content'
import {ListMenuTrigger} from './list-menu-trigger'

/* Context */
import {useHeaderContext} from '../../contexts/header-context'

// Components
import {
    Box,
    Popover,
    PopoverContent,
    PopoverBody,

    // Hooks
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

const ListMenuPopover = ({
    contentComponent,
    item,
    name,
    itemsKey,
    maxColumns,
    categories,
    isActive
}) => {
    const initialFocusRef = useRef()
    const openBlockedRef = useRef(false)
    const {isOpen, onClose, onOpen} = useDisclosure()
    const ContentComponent = contentComponent || ListMenuContent
    const baseStyle = useMultiStyleConfig('ListMenu', {})
    const {shouldShowHeader} = useHeaderContext()

    // Wrap onOpen to check if opening is blocked
    const handleOpen = () => {
        if (!openBlockedRef.current) onOpen()
    }

    useEffect(() => {
        if (!shouldShowHeader && isOpen) {
            onClose()
        }
    }, [shouldShowHeader])

    // Prevent menu from auto-opening when window regains focus
    useEffect(() => {
        const blockOpen = () => {
            onClose()
            openBlockedRef.current = true
            setTimeout(() => {
                openBlockedRef.current = false
            }, 100)
        }
        window.addEventListener('blur', blockOpen)
        window.addEventListener('focus', blockOpen)
        return () => {
            window.removeEventListener('blur', blockOpen)
            window.removeEventListener('focus', blockOpen)
        }
    }, [onClose])

    return (
        <Box onMouseLeave={onClose}>
            <Popover
                trigger="hover"
                openDelay={300}
                placement="bottom-start"
                {...baseStyle.popoverContainer}
                isLazy
                initialFocusRef={initialFocusRef}
                onOpen={handleOpen}
                onClose={onClose}
                isOpen={isOpen}
                variant="fullWidth"
                returnFocusOnClose={false}
                closeOnBlur={false}
            >
                <Fragment>
                    <ListMenuTrigger
                        item={item}
                        name={name}
                        isOpen={isOpen}
                        isActive={isActive}
                        onOpen={onOpen}
                        onClose={onClose}
                    />
                    {isOpen && (
                        <PopoverContent data-testid="popover-menu" {...baseStyle.popoverContent}>
                            <PopoverBody {...baseStyle.popoverBody}>
                                <ContentComponent
                                    item={item}
                                    itemsKey={itemsKey}
                                    initialFocusRef={initialFocusRef}
                                    onClose={onClose}
                                    maxColumns={maxColumns}
                                    categories={categories}
                                />
                            </PopoverBody>
                        </PopoverContent>
                    )}
                </Fragment>
            </Popover>

            {/* Popover overlay */}
            {isOpen && <Box {...baseStyle.popoverOverlay}></Box>}
        </Box>
    )
}

ListMenuPopover.propTypes = {
    contentComponent: PropTypes.elementType,
    item: PropTypes.object,
    name: PropTypes.string,
    maxColumns: PropTypes.number,
    itemsKey: PropTypes.string,
    isActive: PropTypes.bool
}

export {ListMenuPopover}
